// Fatia S5b: flags e kill switches (liga/desliga; kill switches pedem confirmação e motivo).
import { useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import type { AdminFlagRow } from '@impulso/shared/api/admin'
import { errorMessage } from '../../lib/api'
import { ErrorState, Loading, PageHead, Switch } from '../../ui'
import { useToast } from '../../ui/toast'
import { AdminNav } from '../../features/admin/AdminNav'
import { dateTime, useAdminFlags, useSetFlag } from '../../features/admin/api'
import '../../features/account/pages.css'

const RULE_KEY = /^rule\.(R\d+)\.(disabled|shadow)$/

export default function AdminFlags() {
  const q = useAdminFlags()
  const set = useSetFlag()
  const toast = useToast()
  const [pending, setPending] = useState<{ flag: AdminFlagRow; enabled: boolean } | null>(null)
  const [reason, setReason] = useState('')
  const [filter, setFilter] = useState('')

  const apply = (key: string, enabled: boolean, why?: string) =>
    set.mutate({ key, enabled, reason: why }, {
      onSuccess: () => { toast(enabled ? 'Ligado.' : 'Desligado.'); setPending(null); setReason('') },
      onError: (e) => toast(errorMessage(e)),
    })
  const onToggle = (f: AdminFlagRow, enabled: boolean) => {
    if (f.group === 'killswitch') { setPending({ flag: f, enabled }); setReason('') } else apply(f.key, enabled)
  }

  const groups: Array<{ id: AdminFlagRow['group']; title: string; sub: string }> = [
    { id: 'killswitch', title: 'Kill switches', sub: 'Ligado = recurso DESLIGADO para todas as empresas. Use em emergências.' },
    { id: 'other', title: 'Outras', sub: 'Flags gravadas no banco que não estão no catálogo.' },
  ]

  return (
    <>
      <PageHead title="Flags e kill switches" sub="Mudanças valem em até 10 segundos e ficam na auditoria." />
      <AdminNav />
      {q.isLoading && <div className="card"><Loading lines={6} /></div>}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}

      {pending && (
        <div className="callout amber" role="alertdialog" aria-label="Confirmar kill switch" style={{ marginBottom: 16, flexDirection: 'column' }}>
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <TriangleAlert size={18} style={{ flexShrink: 0 }} />
            <div>
              <strong>{pending.enabled ? 'Ligar' : 'Desligar'} "{pending.flag.label}"?</strong>
              <div className="small">{pending.enabled ? 'Isso desliga o recurso para TODAS as empresas imediatamente.' : 'O recurso volta a funcionar para todas as empresas.'}</div>
            </div>
          </div>
          <input className="input" placeholder="Motivo (fica na auditoria)" aria-label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="row wrap">
            <button className="btn primary" disabled={reason.trim().length < 3 || set.isPending} onClick={() => apply(pending.flag.key, pending.enabled, reason.trim())}>Confirmar</button>
            <button className="btn" onClick={() => setPending(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {q.data && (
        <div className="stack">
          {groups.map((g) => {
            // as regras no formato rule.Rn.(disabled|shadow) vão para a tabela; qualquer outra flag de regra cai em "Outras" para não sumir
            const items = q.data.items.filter((f) => !RULE_KEY.test(f.key) && (f.group === g.id || (g.id === 'other' && f.group === 'rule')))
            if (items.length === 0) return null
            return (
              <div key={g.id} className="card flush">
                <div style={{ padding: '16px 16px 8px' }}><h3>{g.title}</h3><p className="small muted">{g.sub}</p></div>
                <div className="list">
                  {items.map((f) => (
                    <div key={f.key} className="list-item" style={{ padding: '12px 16px', alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="small strong">{f.label}</div>
                        <div className="small muted"><code>{f.key}</code>{f.updatedAt ? ` · alterada em ${dateTime(f.updatedAt)}` : ''}</div>
                      </div>
                      {f.group === 'killswitch' && f.enabled && <span className="badge red">ATIVO</span>}
                      <Switch label={f.label} on={f.enabled} disabled={set.isPending} onChange={(v) => onToggle(f, v)} />
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
          <RulesTable flags={q.data.items} filter={filter} onFilter={setFilter} pending={set.isPending} onToggle={onToggle} />
        </div>
      )}
    </>
  )
}

/** Uma linha por regra do motor, com as colunas "Desligada" e "Sombra". */
function RulesTable({ flags, filter, onFilter, pending, onToggle }: { flags: AdminFlagRow[]; filter: string; onFilter: (v: string) => void; pending: boolean; onToggle: (f: AdminFlagRow, v: boolean) => void }) {
  const byRule = new Map<string, { disabled?: AdminFlagRow; shadow?: AdminFlagRow }>()
  for (const f of flags) {
    const m = RULE_KEY.exec(f.key)
    if (!m) continue
    byRule.set(m[1]!, { ...byRule.get(m[1]!), [m[2] as 'disabled' | 'shadow']: f })
  }
  const q = filter.trim().toLowerCase()
  const rows = [...byRule.entries()].filter(([id]) => !q || id.toLowerCase().includes(q))
  if (byRule.size === 0) return null
  return (
    <div className="card flush">
      <div className="row between wrap" style={{ padding: '16px 16px 8px', gap: 12 }}>
        <div>
          <h3>Regras do motor de sugestões</h3>
          <p className="small muted">"Desligada" tira a regra do ar; "Sombra" só registra o que faria, sem agir.</p>
        </div>
        <input className="input" style={{ maxWidth: 220 }} type="search" placeholder="Filtrar regra (ex.: R7)" aria-label="Filtrar regras" value={filter} onChange={(e) => onFilter(e.target.value)} />
      </div>
      {rows.length === 0 ? <p className="small muted" style={{ padding: '0 16px 16px' }}>Nenhuma regra com esse nome.</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Regra</th><th className="center">Desligada</th><th className="center">Sombra</th></tr></thead>
            <tbody>
              {rows.map(([id, r]) => (
                <tr key={id}>
                  <td className="strong">{id}</td>
                  {([['disabled', 'desligada'], ['shadow', 'em modo sombra']] as const).map(([k, txt]) => {
                    const f = r[k]
                    return (
                      <td key={k} className="center">
                        {f ? <span style={{ display: 'inline-flex' }}><Switch label={`Regra ${id} ${txt}`} on={f.enabled} disabled={pending} onChange={(v) => onToggle(f, v)} /></span> : <span className="muted">—</span>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
